import axios from "axios";
import { useAuthStore } from "../store/auth.store.js";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4000/api/v1";

/**
 * Single axios instance for the whole admin app. `withCredentials: true` is
 * required so the HttpOnly refresh cookie travels with /auth/refresh calls.
 */
export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: 30000,
});

// Outgoing requests get the current access token attached as Bearer.
api.interceptors.request.use((config) => {
  const t = useAuthStore.getState().accessToken;
  if (t && !config.skipAuth) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

/**
 * Refresh-on-401 with single-flight: if multiple requests 401 simultaneously,
 * only ONE refresh fires and the rest queue on the same promise. Without this
 * an admin page that issues 5 parallel queries would hammer /auth/refresh.
 */
let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Don't try to refresh on the refresh endpoint itself, on the login
    // endpoint, or on requests we've already retried.
    const isAuthEndpoint =
      original?.url?.includes("/auth/refresh") || original?.url?.includes("/auth/login");

    if (status === 401 && !original?._retry && !isAuthEndpoint) {
      original._retry = true;

      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${BASE_URL}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => {
              refreshPromise = null;
            });
        }
        const refreshResp = await refreshPromise;
        const { accessToken, user } = refreshResp.data?.data ?? {};
        if (!accessToken) throw new Error("Refresh response missing accessToken");

        useAuthStore.getState().setSession(user ?? useAuthStore.getState().user, accessToken);

        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch {
        // Refresh failed → user has to log in again.
        useAuthStore.getState().clear();
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          // Preserve the page they were trying to reach for a post-login redirect.
          const target = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?next=${target}`;
        }
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Unwrap the standard {ok, message, data} envelope. Most callers only care
 * about `data`. We rethrow the response error so React Query's onError sees
 * the actual server message in error.response.data.message.
 */
export async function call(method, path, { data, params, signal, ...rest } = {}) {
  const response = await api.request({ method, url: path, data, params, signal, ...rest });
  return response.data?.data ?? response.data;
}

/**
 * Like `call` but also returns the meta object from paginated responses, so
 * list pages can render pagination controls.
 */
export async function callPaginated(path, params = {}, opts = {}) {
  const response = await api.get(path, { params, ...opts });
  return {
    items: response.data?.data ?? [],
    meta: response.data?.meta ?? { page: 1, limit: 0, total: 0, totalPages: 1 },
  };
}

/**
 * Build an absolute URL from a backend-stored /uploads/... path. Used by every
 * image / PDF the admin renders.
 */
export function assetUrl(relativePath) {
  if (!relativePath) return "";
  if (/^https?:\/\//i.test(relativePath)) return relativePath;
  const base = import.meta.env.VITE_PUBLIC_ASSET_BASE ?? "http://localhost:4000";
  return `${base}${relativePath.startsWith("/") ? "" : "/"}${relativePath}`;
}

/**
 * Resolve a `-thumb`, `-md`, or `-lg` WebP variant for an image. The upload
 * pipeline ships these alongside every original file (e.g. `foo.jpg` →
 * `foo-thumb.webp`, `foo-md.webp`, `foo-lg.webp`), so list and form previews
 * can fetch a 200-px thumbnail instead of a 4 MB original.
 *
 * Falls back to the original URL when the path doesn't have a parseable
 * extension (off-origin URLs, etc.).
 */
export function variantUrl(originalPath, variant = "thumbnail") {
  if (!originalPath) return "";
  if (/^https?:\/\//i.test(originalPath)) return originalPath;
  const suffixes = { thumbnail: "-thumb", md: "-md", lg: "-lg" };
  const suffix = suffixes[variant];
  if (!suffix) return assetUrl(originalPath);
  const dot = originalPath.lastIndexOf(".");
  if (dot === -1) return assetUrl(originalPath);
  const base = originalPath.slice(0, dot);
  return assetUrl(`${base}${suffix}.webp`);
}
